import * as React from "react";
import katex from "katex";
import "katex/dist/katex.min.css";
import {
  AlertCircle,
  AlignLeft,
  Divide,
  Info,
  PencilLine,
  Pi,
  Sigma,
  Square,
  SquareRadical,
  Sun,
  Superscript,
  X as XIcon,
} from "lucide-react";
import type { PublicApplicantAttemptItem } from "@eduviaos/shared/types";

/**
 * Phase F4 BE-3 + F10 — applicant-facing question renderer.
 *
 * Renders one question item from the redacted public attempt DTO. The
 * snapshot shape is BE-controlled and PII-redacted (no answer keys,
 * no grading metadata, no scoring rules), so we render conservatively:
 * unknown fields are ignored and we never reach into question_snapshot
 * keys other than the ones the BE explicitly exposes (see
 * `_REDACTED_SNAPSHOT_KEYS` in `app/domains/admission/public_service.py`).
 *
 * Question types (matches `app/domains/assessment/grader.py`):
 *   - mcq_single, mcq_multi, yes_no, true_false (option-based)
 *   - short_answer, long_answer (free text)
 *   - latex_equation (text-mode LaTeX entry; visual editor is F10 FE)
 *   - numeric (constrained number input)
 *   - match_following (per-pair pairing)
 *   - fill_in_the_blank (per-blank short-answer)
 *   - code, file_upload (graceful fallback — proper UI in F10/F11)
 *
 * Deliberately accepts `unknown` for `response`/`onChange` because the
 * wire shape is type-loose by design; the consumer narrows once.
 */
export function QuestionRenderer({
  item,
  response,
  onChange,
}: {
  item: PublicApplicantAttemptItem;
  response: unknown;
  onChange: (value: unknown) => void;
}) {
  const snap = (item.question_snapshot ?? {}) as Record<string, unknown>;
  const stem = stringField(snap, ["stem", "question", "prompt"]);
  const helpText = stringField(snap, ["help_text", "instructions"]);
  const options = optionList(snap.options ?? snap.choices, item.shuffled_option_order);

  return (
    <div className="grid gap-4">
      <header className="grid gap-1.5">
        {stem && (
          <p className="text-foreground whitespace-pre-wrap text-sm leading-relaxed">{stem}</p>
        )}
        {helpText && (
          <p className="text-muted-foreground inline-flex items-start gap-1.5 text-xs">
            <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
            <span>{helpText}</span>
          </p>
        )}
        <div className="text-muted-foreground text-[11px] uppercase tracking-wide">
          {humaniseType(item.type)} · {item.marks} marks
          {item.negative_marks > 0 && ` · −${item.negative_marks} for incorrect`}
        </div>
      </header>

      {renderInput({ item, snap, options, response, onChange })}
    </div>
  );
}

// ── Per-type input dispatch ────────────────────────────────────────────

function renderInput({
  item,
  snap,
  options,
  response,
  onChange,
}: {
  item: PublicApplicantAttemptItem;
  snap: Record<string, unknown>;
  options: { id: string; label: string }[];
  response: unknown;
  onChange: (value: unknown) => void;
}) {
  switch (item.type) {
    case "mcq_single":
    case "yes_no":
    case "true_false":
      return (
        <SingleChoice
          options={
            item.type === "yes_no"
              ? [
                  { id: "yes", label: "Yes" },
                  { id: "no", label: "No" },
                ]
              : item.type === "true_false"
                ? [
                    { id: "true", label: "True" },
                    { id: "false", label: "False" },
                  ]
                : options
          }
          value={pickStringField(response, ["selected", "value", "choice"])}
          onChange={(value) => onChange({ selected: value })}
        />
      );
    case "mcq_multi":
      return (
        <MultiChoice
          options={options}
          value={pickStringArrayField(response, ["selected", "values", "choices"])}
          onChange={(values) => onChange({ selected: values })}
        />
      );
    case "short_answer":
      return (
        <TextInput
          multiline={false}
          maxLength={getNumber(snap.max_length) ?? 240}
          value={pickStringField(response, ["text", "answer", "value"])}
          onChange={(value) => onChange({ text: value })}
        />
      );
    case "long_answer":
      return (
        <TextInput
          multiline
          maxLength={getNumber(snap.max_length) ?? 5000}
          value={pickStringField(response, ["text", "answer", "value"])}
          onChange={(value) => onChange({ text: value })}
        />
      );
    case "latex_equation":
      return (
        <LatexInput
          maxLength={getNumber(snap.max_length) ?? 1000}
          value={pickStringField(response, ["latex", "text", "answer"])}
          onChange={(value) => onChange({ latex: value })}
        />
      );
    case "numeric":
      return (
        <NumericInput
          unit={getString(snap.unit)}
          min={getNumber(snap.min)}
          max={getNumber(snap.max)}
          value={pickStringField(response, ["value", "number", "answer"])}
          onChange={(value) => onChange({ value })}
        />
      );
    case "match_following":
      return (
        <MatchFollowing
          left={getPairList(snap.left ?? snap.left_items)}
          right={getPairList(snap.right ?? snap.right_items)}
          value={pickRecordField(response, ["pairs", "matches"])}
          onChange={(pairs) => onChange({ pairs })}
        />
      );
    case "fill_in_the_blank":
      return (
        <FillBlanks
          blanks={getBlanks(snap.blanks ?? snap.blank_keys)}
          value={pickRecordField(response, ["blanks", "answers"])}
          onChange={(blanks) => onChange({ blanks })}
        />
      );
    case "code":
      return (
        <CodeInput
          language={getString(snap.language) ?? "plaintext"}
          maxLength={getNumber(snap.max_length) ?? 20000}
          value={pickStringField(response, ["code", "text", "answer"])}
          onChange={(value) => onChange({ code: value })}
        />
      );
    case "file_upload":
      return <FileUploadFallback />;
    default:
      return (
        <div className="border-border/60 bg-muted/30 text-muted-foreground rounded-md border p-3 text-xs">
          <AlertCircle className="text-foreground/70 mb-1.5 h-4 w-4" />
          This question type ({item.type}) isn't supported in this version of the apply portal yet.
          Please contact the institution to take this assessment in person.
        </div>
      );
  }
}

// ── Input primitives ──────────────────────────────────────────────────

function SingleChoice({
  options,
  value,
  onChange,
}: {
  options: { id: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
}) {
  if (options.length === 0) {
    return <EmptyOptionsNotice />;
  }
  return (
    <div role="radiogroup" className="grid gap-2">
      {options.map((opt) => {
        const checked = value === opt.id;
        return (
          <label
            key={opt.id}
            className={`flex cursor-pointer items-start gap-2 rounded-md border p-3 text-sm transition-colors ${
              checked
                ? "border-primary bg-primary/5"
                : "border-border bg-card hover:border-foreground/30"
            }`}
          >
            <input
              type="radio"
              name={`q-${opt.id}`}
              checked={checked}
              onChange={() => onChange(opt.id)}
              className="mt-0.5"
            />
            <span className="text-foreground">{opt.label}</span>
          </label>
        );
      })}
    </div>
  );
}

function MultiChoice({
  options,
  value,
  onChange,
}: {
  options: { id: string; label: string }[];
  value: string[];
  onChange: (value: string[]) => void;
}) {
  if (options.length === 0) {
    return <EmptyOptionsNotice />;
  }
  const selected = new Set(value);
  return (
    <div className="grid gap-2">
      {options.map((opt) => {
        const checked = selected.has(opt.id);
        return (
          <label
            key={opt.id}
            className={`flex cursor-pointer items-start gap-2 rounded-md border p-3 text-sm transition-colors ${
              checked
                ? "border-primary bg-primary/5"
                : "border-border bg-card hover:border-foreground/30"
            }`}
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => {
                const next = new Set(selected);
                if (e.target.checked) next.add(opt.id);
                else next.delete(opt.id);
                onChange(Array.from(next).sort());
              }}
              className="mt-0.5"
            />
            <span className="text-foreground">{opt.label}</span>
          </label>
        );
      })}
    </div>
  );
}

function TextInput({
  multiline,
  maxLength,
  value,
  onChange,
}: {
  multiline: boolean;
  maxLength: number;
  value: string;
  onChange: (value: string) => void;
}) {
  const Element = multiline ? "textarea" : "input";
  return (
    <div className="grid gap-1">
      <Element
        // The element type is one of two (input | textarea); both
        // accept className/value/onChange/maxLength so casting via
        // unknown is the cleanest way to satisfy the union here.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        {...({ rows: multiline ? 6 : undefined } as any)}
        className="border-border bg-card text-foreground focus:border-primary w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-1"
        value={value}
        maxLength={maxLength}
        onChange={(e) => onChange(e.target.value)}
        spellCheck={multiline}
      />
      <div className="text-muted-foreground text-right text-[11px] tabular-nums">
        {value.length} / {maxLength}
      </div>
    </div>
  );
}

/** Visual LaTeX editor (Phase F10 FE).
 *
 *  Two-pane layout: a monospace textarea for LaTeX source on the left
 *  (or top on small screens), live KaTeX-rendered preview on the right
 *  (or below). A toolbar above the textarea inserts common LaTeX
 *  tokens (fractions, roots, exponents, integrals, Greek letters)
 *  around the current selection — so highlight `1` then click `\frac{}{}`
 *  to wrap into `\frac{1}{}` with the cursor parked in the second slot.
 *
 *  Errors from KaTeX (malformed LaTeX) surface as a small chip in the
 *  preview pane rather than crashing the assessment runner. KaTeX
 *  ParseError messages are user-friendly enough to render directly.
 *
 *  We deliberately do not lock applicants into an Equation Editor
 *  (e.g. MathLive) — text-mode entry remains available for those
 *  who'd rather type LaTeX directly. The toolbar is opt-in. */
function LatexInput({
  maxLength,
  value,
  onChange,
}: {
  maxLength: number;
  value: string;
  onChange: (value: string) => void;
}) {
  const taRef = React.useRef<HTMLTextAreaElement | null>(null);

  const insert = React.useCallback(
    (tokens: { snippet: string; cursorOffset?: number }) => {
      const ta = taRef.current;
      if (!ta) {
        // Fallback path for env without a textarea ref (SSR / tests):
        // append at the end and accept the cursor lands wherever.
        const next = (value + tokens.snippet).slice(0, maxLength);
        onChange(next);
        return;
      }
      const start = ta.selectionStart ?? value.length;
      const end = ta.selectionEnd ?? value.length;
      const selection = value.slice(start, end);
      const snippet = tokens.snippet.replace("$1", selection);
      const next = (value.slice(0, start) + snippet + value.slice(end)).slice(0, maxLength);
      onChange(next);
      // Restore focus + place caret right where the user expects it.
      requestAnimationFrame(() => {
        if (!taRef.current) return;
        taRef.current.focus();
        const caret =
          start + (tokens.cursorOffset !== undefined ? tokens.cursorOffset : snippet.length);
        const safeCaret = Math.min(caret, taRef.current.value.length);
        taRef.current.setSelectionRange(safeCaret, safeCaret);
      });
    },
    [value, onChange, maxLength],
  );

  return (
    <div className="grid gap-2">
      <p className="text-muted-foreground inline-flex items-start gap-1.5 text-xs">
        <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
        <span>
          Type LaTeX directly, or use the toolbar to insert common tokens. The right pane shows the
          rendered equation as you type.
        </span>
      </p>

      <LatexToolbar onInsert={insert} />

      <div className="grid gap-2 sm:grid-cols-2">
        <label className="grid gap-1.5">
          <span className="text-muted-foreground text-[11px] uppercase tracking-wide">
            <PencilLine className="mr-1 inline h-3 w-3" aria-hidden="true" />
            LaTeX source
          </span>
          <textarea
            ref={taRef}
            className="border-border bg-card text-foreground focus:border-primary min-h-[100px] w-full rounded-md border px-3 py-2 font-mono text-sm focus:outline-none focus:ring-1"
            value={value}
            maxLength={maxLength}
            rows={4}
            onChange={(e) => onChange(e.target.value)}
            spellCheck={false}
            aria-label="LaTeX source"
          />
        </label>
        <div className="grid gap-1.5">
          <span className="text-muted-foreground text-[11px] uppercase tracking-wide">
            <Sun className="mr-1 inline h-3 w-3" aria-hidden="true" />
            Preview
          </span>
          <LatexPreview source={value} />
        </div>
      </div>

      <div className="text-muted-foreground text-right text-[11px] tabular-nums">
        {value.length} / {maxLength}
      </div>
    </div>
  );
}

interface LatexToolbarItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string; "aria-hidden"?: boolean | "true" | "false" }>;
  /** LaTeX snippet to insert. Use `$1` to mark where the current
   *  text selection should be placed. */
  snippet: string;
  /** Offset (chars from `start`) where the caret should land. If
   *  omitted, the caret lands after the inserted snippet. */
  cursorOffset?: number;
}

const LATEX_TOOLBAR: LatexToolbarItem[] = [
  {
    id: "frac",
    label: "Fraction · \\frac{a}{b}",
    icon: Divide,
    snippet: "\\frac{$1}{}",
    cursorOffset: 6,
  },
  {
    id: "sqrt",
    label: "Square root · \\sqrt{a}",
    icon: SquareRadical,
    snippet: "\\sqrt{$1}",
    cursorOffset: 6,
  },
  {
    id: "sup",
    label: "Exponent · a^{b}",
    icon: Superscript,
    snippet: "$1^{}",
    cursorOffset: 3,
  },
  {
    id: "sub",
    label: "Subscript · a_{b}",
    icon: AlignLeft,
    snippet: "$1_{}",
    cursorOffset: 3,
  },
  {
    id: "sum",
    label: "Sum · \\sum_{i=1}^{n}",
    icon: Sigma,
    snippet: "\\sum_{i=1}^{n} $1",
    cursorOffset: 14,
  },
  {
    id: "int",
    label: "Integral · \\int_{a}^{b}",
    icon: Square,
    snippet: "\\int_{}^{} $1",
    cursorOffset: 6,
  },
  {
    id: "pi",
    label: "Greek pi · \\pi",
    icon: Pi,
    snippet: "\\pi ",
  },
  {
    id: "times",
    label: "Multiplication · \\times",
    icon: XIcon,
    snippet: "\\times ",
  },
];

function LatexToolbar({
  onInsert,
}: {
  onInsert: (tokens: { snippet: string; cursorOffset?: number }) => void;
}) {
  return (
    <div
      role="toolbar"
      aria-label="LaTeX quick-insert"
      className="border-border bg-muted/20 flex flex-wrap gap-1 rounded-md border p-1.5"
    >
      {LATEX_TOOLBAR.map((item) => (
        <button
          key={item.id}
          type="button"
          title={item.label}
          aria-label={item.label}
          onClick={() => onInsert({ snippet: item.snippet, cursorOffset: item.cursorOffset })}
          className="border-border/40 bg-card text-foreground hover:border-primary/60 hover:bg-primary/5 inline-flex h-7 w-7 items-center justify-center rounded border text-xs"
        >
          <item.icon className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      ))}
    </div>
  );
}

function LatexPreview({ source }: { source: string }) {
  const trimmed = source.trim();
  const html = React.useMemo(() => {
    if (!trimmed) return { kind: "empty" as const };
    try {
      return {
        kind: "ok" as const,
        html: katex.renderToString(trimmed, {
          throwOnError: true,
          strict: "ignore",
          displayMode: true,
          output: "htmlAndMathml",
          // Keep the trust list tight — KaTeX off-by-default for
          // `\href`, `\url`, etc. We never want an applicant's LaTeX
          // to render a clickable link.
          trust: false,
        }),
      };
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      return { kind: "error" as const, message: msg };
    }
  }, [trimmed]);

  if (html.kind === "empty") {
    return (
      <div className="border-border/60 bg-muted/10 text-muted-foreground flex min-h-[100px] items-center justify-center rounded-md border border-dashed px-3 py-2 text-xs">
        Preview appears here.
      </div>
    );
  }

  if (html.kind === "error") {
    return (
      <div className="border-destructive/40 bg-destructive/5 text-destructive grid min-h-[100px] gap-1 rounded-md border px-3 py-2 text-xs">
        <span className="inline-flex items-center gap-1.5 font-medium">
          <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" />
          LaTeX could not render
        </span>
        <span className="font-mono text-[11px] leading-relaxed opacity-90">{html.message}</span>
      </div>
    );
  }

  return (
    <div
      className="border-border bg-card text-foreground min-h-[100px] overflow-x-auto rounded-md border px-3 py-2 text-sm"
      // Safe: KaTeX renders to its own controlled markup; trust is off
      // and strict='ignore' means no raw HTML escapes through. We never
      // forward this string to anywhere except the React tree.
      dangerouslySetInnerHTML={{ __html: html.html }}
    />
  );
}

function NumericInput({
  unit,
  min,
  max,
  value,
  onChange,
}: {
  unit: string | null;
  min: number | null;
  max: number | null;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="grid gap-1">
      <div className="flex items-center gap-2">
        <input
          type="number"
          inputMode="decimal"
          step="any"
          className="border-border bg-card text-foreground focus:border-primary flex-1 rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-1"
          value={value}
          {...(min !== null ? { min } : {})}
          {...(max !== null ? { max } : {})}
          onChange={(e) => onChange(e.target.value)}
        />
        {unit && <span className="text-muted-foreground text-sm">{unit}</span>}
      </div>
      {(min !== null || max !== null) && (
        <p className="text-muted-foreground text-[11px]">
          Acceptable range: {min ?? "−∞"} to {max ?? "+∞"}
        </p>
      )}
    </div>
  );
}

function MatchFollowing({
  left,
  right,
  value,
  onChange,
}: {
  left: { id: string; label: string }[];
  right: { id: string; label: string }[];
  value: Record<string, unknown>;
  onChange: (pairs: Record<string, string>) => void;
}) {
  if (left.length === 0 || right.length === 0) {
    return <EmptyOptionsNotice />;
  }
  // Coerce the wire shape (any Record<string, unknown>) into our
  // local string→string map so we can render and update it safely.
  const pairs: Record<string, string> = {};
  for (const key of Object.keys(value)) {
    const v = value[key];
    if (typeof v === "string") pairs[key] = v;
  }

  return (
    <div className="grid gap-2.5">
      {left.map((l) => (
        <div key={l.id} className="flex flex-wrap items-center gap-2">
          <span className="text-foreground min-w-[40%] text-sm">{l.label}</span>
          <span className="text-muted-foreground">→</span>
          <select
            className="border-border bg-card text-foreground focus:border-primary flex-1 rounded-md border px-2 py-1.5 text-sm focus:outline-none focus:ring-1"
            value={pairs[l.id] ?? ""}
            onChange={(e) => {
              const next = { ...pairs };
              if (e.target.value === "") delete next[l.id];
              else next[l.id] = e.target.value;
              onChange(next);
            }}
          >
            <option value="">— choose —</option>
            {right.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </select>
        </div>
      ))}
    </div>
  );
}

function FillBlanks({
  blanks,
  value,
  onChange,
}: {
  blanks: string[];
  value: Record<string, unknown>;
  onChange: (blanks: Record<string, string>) => void;
}) {
  if (blanks.length === 0) {
    return <EmptyOptionsNotice />;
  }
  const cur: Record<string, string> = {};
  for (const key of Object.keys(value)) {
    const v = value[key];
    if (typeof v === "string") cur[key] = v;
  }
  return (
    <div className="grid gap-2.5">
      {blanks.map((blankKey, idx) => (
        <div key={blankKey} className="flex flex-wrap items-center gap-2">
          <label className="text-foreground min-w-[5rem] text-sm font-medium">
            Blank {idx + 1}
          </label>
          <input
            type="text"
            className="border-border bg-card text-foreground focus:border-primary flex-1 rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-1"
            value={cur[blankKey] ?? ""}
            maxLength={240}
            onChange={(e) => {
              const next = { ...cur };
              if (e.target.value === "") delete next[blankKey];
              else next[blankKey] = e.target.value;
              onChange(next);
            }}
          />
        </div>
      ))}
    </div>
  );
}

function CodeInput({
  language,
  maxLength,
  value,
  onChange,
}: {
  language: string;
  maxLength: number;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="grid gap-1.5">
      <p className="text-muted-foreground inline-flex items-start gap-1.5 text-xs">
        <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
        <span>
          Code questions are reviewed manually by the institution. Submit your{" "}
          <span className="font-medium">{language}</span> code below.
        </span>
      </p>
      <textarea
        className="border-border bg-card text-foreground focus:border-primary w-full rounded-md border px-3 py-2 font-mono text-sm focus:outline-none focus:ring-1"
        value={value}
        maxLength={maxLength}
        rows={12}
        onChange={(e) => onChange(e.target.value)}
        spellCheck={false}
        wrap="off"
      />
      <div className="text-muted-foreground text-right text-[11px] tabular-nums">
        {value.length} / {maxLength}
      </div>
    </div>
  );
}

function FileUploadFallback() {
  return (
    <div className="border-border/60 bg-muted/30 text-muted-foreground rounded-md border p-3 text-xs">
      <AlertCircle className="text-foreground/70 mb-1.5 h-4 w-4" />
      This question requires a file upload. The upload UI will be available in a future release —
      for now, please contact the institution to submit your answer directly.
    </div>
  );
}

function EmptyOptionsNotice() {
  return (
    <div className="border-border/60 bg-muted/30 text-muted-foreground rounded-md border p-3 text-xs">
      <AlertCircle className="text-foreground/70 mb-1.5 h-4 w-4" />
      No options were provided for this question. Please contact the institution.
    </div>
  );
}

// ── Helpers — narrow loose wire types into renderable shapes ───────────

function humaniseType(type: string): string {
  return type
    .replace(/_/g, " ")
    .replace(/\bmcq\b/i, "Multiple choice")
    .replace(/\blatex\b/i, "LaTeX")
    .replace(/^\w/, (c) => c.toUpperCase());
}

function stringField(snap: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const v = snap[key];
    if (typeof v === "string" && v.trim() !== "") return v;
  }
  return "";
}

function getString(value: unknown): string | null {
  return typeof value === "string" && value !== "" ? value : null;
}

function getNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value !== "") {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function optionList(raw: unknown, shuffledOrder: string[] | null): { id: string; label: string }[] {
  if (!Array.isArray(raw)) return [];
  const parsed: { id: string; label: string }[] = [];
  for (const entry of raw) {
    if (typeof entry === "string") {
      parsed.push({ id: entry, label: entry });
      continue;
    }
    if (entry && typeof entry === "object") {
      const obj = entry as Record<string, unknown>;
      const id = getString(obj.id) ?? getString(obj.key) ?? getString(obj.value);
      const label = getString(obj.label) ?? getString(obj.text) ?? id;
      if (id) parsed.push({ id, label: label ?? id });
    }
  }
  if (!shuffledOrder || shuffledOrder.length === 0) return parsed;
  // Reorder by shuffledOrder if present (BE may pre-shuffle option
  // ids and pass the order via `shuffled_option_order`).
  const byId = new Map(parsed.map((p) => [p.id, p]));
  const ordered: { id: string; label: string }[] = [];
  for (const id of shuffledOrder) {
    const found = byId.get(id);
    if (found) {
      ordered.push(found);
      byId.delete(id);
    }
  }
  // Append any options not mentioned in shuffledOrder (defensive).
  for (const remaining of byId.values()) ordered.push(remaining);
  return ordered;
}

function getPairList(value: unknown): { id: string; label: string }[] {
  if (!Array.isArray(value)) return [];
  const out: { id: string; label: string }[] = [];
  for (const entry of value) {
    if (typeof entry === "string") {
      out.push({ id: entry, label: entry });
      continue;
    }
    if (entry && typeof entry === "object") {
      const obj = entry as Record<string, unknown>;
      const id = getString(obj.id) ?? getString(obj.key);
      const label = getString(obj.label) ?? getString(obj.text) ?? id;
      if (id) out.push({ id, label: label ?? id });
    }
  }
  return out;
}

function getBlanks(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const out: string[] = [];
  for (const entry of value) {
    if (typeof entry === "string") out.push(entry);
    else if (entry && typeof entry === "object") {
      const obj = entry as Record<string, unknown>;
      const k = getString(obj.id) ?? getString(obj.key) ?? getString(obj.name);
      if (k) out.push(k);
    }
  }
  return out;
}

function pickStringField(value: unknown, keys: string[]): string {
  if (!value || typeof value !== "object") return "";
  const obj = value as Record<string, unknown>;
  for (const key of keys) {
    const v = obj[key];
    if (typeof v === "string") return v;
    if (typeof v === "number") return String(v);
  }
  return "";
}

function pickStringArrayField(value: unknown, keys: string[]): string[] {
  if (!value || typeof value !== "object") return [];
  const obj = value as Record<string, unknown>;
  for (const key of keys) {
    const v = obj[key];
    if (Array.isArray(v)) {
      return v.filter((x): x is string => typeof x === "string");
    }
  }
  return [];
}

function pickRecordField(value: unknown, keys: string[]): Record<string, unknown> {
  if (!value || typeof value !== "object") return {};
  const obj = value as Record<string, unknown>;
  for (const key of keys) {
    const v = obj[key];
    if (v && typeof v === "object" && !Array.isArray(v)) {
      return v as Record<string, unknown>;
    }
  }
  return {};
}
